import { getTemporarySave, postTemporarySave } from "@/api/ticket";
import { useCallback, useEffect, useRef } from "react";

interface UseTemporarySaveParams {
  userId?: number;
  dataTicketId?: number;
  comment: string;
  reset: (values?: any) => void;
  defaultValues?: any;
  isSubmitting?: boolean;
}

export const useTemporarySave = ({
  userId,
  dataTicketId,
  comment,
  reset,
  defaultValues = { ticket_destinations_id: -1 },
  isSubmitting = false,
}: UseTemporarySaveParams) => {
  const debounceRef = useRef<NodeJS.Timeout>();
  const resetRef = useRef(reset);
  const defaultValuesRef = useRef(defaultValues);

  // refs を最新の値で更新
  useEffect(() => {
    resetRef.current = reset;
    defaultValuesRef.current = defaultValues;
  });

  // 一時保存データを取得してフォームに設定する関数
  const loadTemporarySave = useCallback(() => {
    if (!userId || !dataTicketId) return;

    getTemporarySave({
      user_id: userId,
      data_ticket_id: dataTicketId,
    }).then((res) => {
      if (
        res.data?.status === 1 &&
        res.data?.data &&
        res.data.data.comment !== undefined
      ) {
        // resetを使用してフォーム全体を初期化し、一時保存データを設定
        resetRef.current({
          ...defaultValuesRef.current,
          comment: res.data.data.comment,
        });
      }
    });
  }, [userId, dataTicketId]);

  // 初期データの読み込み
  useEffect(() => {
    if (!userId || !dataTicketId || isSubmitting) return;
    loadTemporarySave();
  }, [userId, dataTicketId, isSubmitting]);

  // キャンセル後の一時保存データ復元イベントのリスナー
  useEffect(() => {
    const handleCommentCanceled = (event: CustomEvent) => {
      const { dataTicketId: canceledDataTicketId, userId: canceledUserId } =
        event.detail;

      // 自分のDRM-Chatの場合のみ復元処理を実行
      if (canceledDataTicketId === dataTicketId && canceledUserId === userId) {
        loadTemporarySave();
      }
    };

    window.addEventListener(
      "ticketCommentCanceled",
      handleCommentCanceled as EventListener
    );

    return () => {
      window.removeEventListener(
        "ticketCommentCanceled",
        handleCommentCanceled as EventListener
      );
    };
  }, [dataTicketId, userId]);

  // 一時保存の実行
  const saveTemporary = useCallback(() => {
    if (!userId || !dataTicketId) return;

    postTemporarySave({
      user_id: userId,
      data_ticket_id: dataTicketId,
      comment,
    });
  }, [userId, dataTicketId, comment]);

  // コメントが変更されたときの処理（3秒のdebounce）
  useEffect(() => {
    if (!comment || !userId || !dataTicketId || isSubmitting) return;

    // 既存のタイマーをクリア
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // 3秒後に一時保存を実行
    debounceRef.current = setTimeout(() => {
      saveTemporary();
    }, 3000);

    // クリーンアップ
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [comment, userId, dataTicketId, isSubmitting]);
};
